import '../../lang/index.js';
import { registry } from '../../lang/registry.js';
import { matchRelationSelector, normalizeRelationSelectorArg } from '../../clients/abstracts/util.js';
import { DEFAULT_USERSPACE_DATA } from './bootstrap/catalog.bootstrap.js';
import { FlashQLSchemaInference } from './FlashQLSchemaInference.js';
import { FlashQlWal } from './FlashQlWal.js';
import { SyncManager } from '../sync/SyncManager.js';
import { MVCCEngine } from './MVCCEngine.js';
import { SYSTEM_TAG } from '../../proc/SYSTEM.js';

export class StorageEngine extends MVCCEngine {

    #flashQlClient;
    #dialect;
    #keyval;
    #options;
    #wal;
    #sync;

    #autoSync;
    #forcedReadOnly;
    #readOnly;
    #overwriteForward;
    #forwardHistoryTruncated;
    #openedCommitTime = null;

    #getUpstreamClient;
    #foreignClients = new Map;

    #catalog = new Map;

    #isHydrating;
    #initCalled;

    get flashQlClient() { return this.#flashQlClient; }
    get dialect() { return this.#dialect; }
    get keyval() { return this.#keyval; }
    get options() { return { ...this.#options }; }
    get wal() { return this.#wal; }
    get sync() { return this.#sync; }

    get readOnly() { return this.#readOnly; }
    get openedCommitTime() { return this.#openedCommitTime; }

    get _isHydrating() { return this.#isHydrating; }
    get _catalog() { return this.#catalog; }

    constructor({ flashQlClient = null, dialect = 'postgres', keyval = null, autoSync = true, getUpstreamClient = null, readOnly = false, ...options } = {}) {
        super();

        this.#flashQlClient = flashQlClient;
        this.#dialect = dialect;
        this.#keyval = keyval;
        this.#options = options;

        this.#autoSync = !!autoSync;
        this.#getUpstreamClient = getUpstreamClient;

        this.#forcedReadOnly = !!readOnly;
        this.#readOnly = this.#forcedReadOnly;
        this.#overwriteForward = false;
        this.#forwardHistoryTruncated = true;

        this.#wal = new FlashQlWal({ storageEngine: this, keyval: keyval ?? undefined, drainMode: 'never' });
        this.#sync = new SyncManager(this);
    }

    async open({ versionStop = null, overwriteForward = false } = {}) {
        if (this.#initCalled) return;
        this.#initCalled = true;

        if (overwriteForward && !versionStop) {
            throw new TypeError('open({ overwriteForward }) requires versionStop');
        }
        if (this.#forcedReadOnly && overwriteForward) {
            throw new TypeError('Cannot use overwriteForward when engine is configured readOnly');
        }
        if (versionStop) {
            this.#overwriteForward = !!overwriteForward;
            this.#readOnly = this.#forcedReadOnly || !overwriteForward;
            this.#forwardHistoryTruncated = !overwriteForward;
        } else {
            this.#overwriteForward = false;
            this.#readOnly = this.#forcedReadOnly;
            this.#forwardHistoryTruncated = true;
        }

        if (typeof versionStop === 'string') {
            const tblRefNode = await registry.TableRef1.parse(versionStop);
            if (!tblRefNode)
                throw new TypeError(`Invalid version stop argument ${versionStop}`);
            versionStop = {
                namespace: tblRefNode.qualifier()?.value(),
                name: tblRefNode.value(),
                versionSpec: tblRefNode.versionSpec()?.value(),
            };
            if (!versionStop.namespace)
                throw new TypeError(`Version stop table spec must include namespace qualification: ${versionStop}`);
        } else if (versionStop) {
            if (typeof versionStop !== 'object')
                throw new TypeError('Version stop argument must be either a string or an object');
            for (const k in versionStop) {
                if (k !== 'namespace' && k !== 'name' && k !== 'versionSpec')
                    throw new TypeError(`Unexpected property ${k} in version stop argument`);
            }
            if (!versionStop.namespace || !versionStop.name)
                throw new TypeError('Version stop object must include both "namespace" and "name"');
        }

        const throwNoMatch = () => {
            throw new Error(`No table version matched ${JSON.stringify(versionStop.namespace)}.${JSON.stringify(versionStop.name)}${versionStop.versionSpec ? '@' + versionStop.versionSpec : ''} to boot to`);
        }

        if (await this.#wal.latestCommit()) {
            if (versionStop) {
                const { lastMatchedCommitTime } = await this.#hydrateFromPersistence({ versionStop, findLastMatch: true });
                if (!lastMatchedCommitTime) throwNoMatch();

                this.#catalog = new Map;
                this.#sequences.clear();

                const { lastReplayedCommitTime } = await this.#hydrateFromPersistence({ stopAtCommitTime: lastMatchedCommitTime });
                this.#openedCommitTime = lastReplayedCommitTime;
            } else {
                const { lastReplayedCommitTime } = await this.#hydrateFromPersistence({});
                this.#openedCommitTime = lastReplayedCommitTime;
            }
        } else {
            await this.#hydrateFromDefaults();
            if (versionStop) {
                const hasMatch = await this.transaction(async (tx) => {
                    return !!tx.showTable(versionStop, { ifExists: true });
                });
                if (!hasMatch) throwNoMatch();
            }
            this.#openedCommitTime = await this.#wal.latestCommit();
        }

        if (this.#autoSync && this.#keyval) {
            await this.#sync.sync();
        }
    }

    async close({ destroy = false } = {}) {
        await this.#sync.shutdown();
        await this.#wal.close({ destroy });
    }

    getResolver() {
        return new FlashQLSchemaInference({ storageEngine: this });
    }

    // ----------

    async getUpstreamClient(origin) {
        if (!this.#getUpstreamClient)
            throw new Error('Cannot process foreign operation; missing options.getUpstreamClient');
        if (!this.#foreignClients.has(origin)) {
            this.#foreignClients.set(origin, await this.#getUpstreamClient(origin));
        }
        return this.#foreignClients.get(origin);
    }

    async getEffectiveClient(tblDef, assert = true) {
        const replicationAttrs = tblDef.view_mode_replication_attrs;
        const effectiveReplicationOrigin = replicationAttrs.effective_replication_origin;
        if (effectiveReplicationOrigin)
            return await this.getUpstreamClient(effectiveReplicationOrigin);
        if (this.#flashQlClient) return this.#flashQlClient;
        if (assert) throw new Error('Operation requires a source flashQlClient; configure StorageEngine with options.flashQlClient or namespace replication origins');
    }

    async getSourceResolver(tblDef) {
        if (tblDef.kind !== 'view') return this.getResolver();
        return (await this.getEffectiveClient(tblDef)).resolver;
    }

    // ----- bootloader/WAL

    async #hydrateFromDefaults() {
        await this.transaction(async (tx) =>
            await tx.replay(DEFAULT_USERSPACE_DATA));
    }

    async #hydrateFromPersistence({ versionStop = null, findLastMatch = false, stopAtCommitTime = null } = {}) {
        this.#isHydrating = true;
        let lastMatchedCommitTime = null;
        let lastReplayedCommitTime = null;
        let pendingEntries = [];

        try {
            for await (const commit of this.#wal.streamCommits()) {
                if (Number.isInteger(stopAtCommitTime) && commit.commitTime > stopAtCommitTime) {
                    break;
                }

                await this.transaction(async (tx) => {
                    for (const event of commit.entries) {
                        try {
                            await tx.replay([event]);
                        } catch (e) {
                            if (!this.#canDeferHydrationEvent(event, e)) throw e;
                            pendingEntries.push(event);
                        }
                    }

                    if (pendingEntries.length) {
                        pendingEntries = await this.#drainPendingHydrationEntries(tx, pendingEntries);
                    }

                    if (findLastMatch && versionStop) {
                        const tblDef = tx.showTable(versionStop, { ifExists: true });
                        if (tblDef) {
                            lastMatchedCommitTime = commit.commitTime;
                        }
                    }
                });

                const sequenceHeads = commit.sequenceHeads || {};
                for (const [seqId, seqValue] of Object.entries(sequenceHeads)) {
                    this.#sequences.set(seqId, seqValue);
                }
                lastReplayedCommitTime = commit.commitTime;
            }

            if (pendingEntries.length) {
                await this.transaction(async (tx) => {
                    pendingEntries = await this.#drainPendingHydrationEntries(tx, pendingEntries, { assertFullyDrained: true });
                });
            }
        } finally {
            this.#isHydrating = false;
        }

        return { lastMatchedCommitTime, lastReplayedCommitTime };
    }

    #canDeferHydrationEvent(event, error) {
        if (!this.#isHydrating) return false;
        const message = String(error?.message || error);
        return /does not exist/.test(message)
            && !!event?.relation?.namespace
            && !!event?.relation?.name;
    }

    async #drainPendingHydrationEntries(tx, entries, { assertFullyDrained = false } = {}) {
        let pending = [...entries];
        let progressed = true;

        while (pending.length && progressed) {
            progressed = false;
            const deferred = [];

            for (const event of pending) {
                try {
                    await tx.replay([event]);
                    progressed = true;
                } catch (e) {
                    if (!this.#canDeferHydrationEvent(event, e)) throw e;
                    deferred.push(event);
                }
            }

            pending = deferred;
        }

        if (assertFullyDrained && pending.length) {
            const unresolved = pending[0];
            throw new Error(`Hydration could not resolve persisted event for relation ${JSON.stringify(unresolved.relation.namespace)}.${JSON.stringify(unresolved.relation.name)}`);
        }

        return pending;
    }

    async #federateCommit(tx, timestamp) {
        const commitTime = this.txMeta(tx.id)?.commitTime;

        const federateCommit = async (targetOrigin, entries, { queued = false } = {}) => {
            if (queued) {
                const outsyncQueue = tx.getRelation({ namespace: 'sys', name: 'sys_outsync_queue' });
                for (const changePayload of entries) {
                    await outsyncQueue.insert({
                        relation_id: changePayload.relation_id,
                        origin: changePayload.origin,
                        event_payload: changePayload.event,
                        status: 'pending',
                        retry_count: 0,
                        last_error: null,
                        created_at: timestamp,
                        updated_at: timestamp,
                        next_retry_at: null,
                    }, { systemTag: SYSTEM_TAG });
                }
                return;
            }

            const commit = {
                txId: tx.id,
                commitTime,
                entries: entries.map((changePayload) => changePayload.event),
                timestamp
            };

            return targetOrigin
                ? (await this.getUpstreamClient(targetOrigin)).wal.applyDownstreamCommit(commit)
                : await this.wal.applyDownstreamCommit(commit, { tx });
        };

        for (const [targetOrigin, entries] of tx._upstreamQueue) {
            await federateCommit(targetOrigin, entries, { queued: true });
        }

        for (const [targetOrigin, entries] of tx._upstreamLog) {
            await federateCommit(targetOrigin, entries);
        }
    }

    #queuedSyncSelectorFor(tx) {
        if (!tx._upstreamQueue.size) return null;

        const selector = {};
        for (const entries of tx._upstreamQueue.values()) {
            for (const changePayload of entries) {
                const relation = tx.getRelation({ id: changePayload.relation_id });
                if (!relation?.namespace || !relation?.name) continue;
                if (!selector[relation.namespace]) selector[relation.namespace] = [];
                if (!selector[relation.namespace].includes(relation.name)) {
                    selector[relation.namespace].push(relation.name);
                }
            }
        }

        return Object.keys(selector).length ? selector : null;
    }

    #scheduleAutoSync(selector) {
        if (!selector) return;
        void this.#sync.sync(selector).catch(() => {});
    }

    async #persistCommit(tx, timestamp) {
        const commitTime = this.txMeta(tx.id)?.commitTime;

        const entries = structuredClone(tx._changeLog);
        const sequenceHeads = Object.fromEntries([
            ...tx._affectedSequences
        ].map((seqId) => [seqId, this.#sequences.get(seqId)]));

        const commit = { txId: tx.id, commitTime, entries, sequenceHeads, timestamp };
        await this.#wal.dispatch(commit);
    }

    // ----- sessionConfig

    #sessionConfig = new Map;

    setSessionConfig(name, value, tx = null) {
        if ([].concat(value).some((x) => typeof x !== 'string' && !(x instanceof String))) {
            throw new Error(`[${name}] Config value must be a string or an array of strings`);
        }

        const prev = this.#sessionConfig.get(name);
        // We only store String instances internally for instance checks later
        const _new = typeof value === 'string'
            ? new String(value)
            : (typeof value === 'number'
                ? new Number(value)
                : value);

        this.#sessionConfig.set(name, _new);

        tx?.addUndo(() => {
            // Instance checks
            if (this.#sessionConfig.get(name) !== _new) return;
            this.#sessionConfig.set(name, prev);
        });
    }

    getSessionConfig(name, tx = null) {
        const value = this.#sessionConfig.get(name);
        return value instanceof String
            ? value + ''
            : (value instanceof Number
                ? value + 0
                : value);
    }

    defaultNamespace(tx = null) {
        const searchPath = this.getSessionConfig('search_path', tx) || [];
        if (searchPath.length) return searchPath[0];
        return 'public';
    }

    // ----- sequences

    #sequences = new Map;

    _nextSequence(seqId) {
        if (!this.#sequences.has(seqId)) {
            this.#sequences.set(seqId, 1);
        }

        const v = this.#sequences.get(seqId);
        this.#sequences.set(seqId, v + 1);

        return v;
    }

    _ensureSequenceAtLeast(seqId, nextValue) {
        if (!Number.isInteger(nextValue)) return;
        const current = this.#sequences.get(seqId) || 1;
        if (nextValue > current) {
            this.#sequences.set(seqId, nextValue);
        }
    }

    // ----- Schemas

    async _resolveRelationSelector(selector, handle, { handlerMode = 'sync', tx: inputTx = null } = {}) {
        const tx = this.begin({ parentTx: inputTx });
        const isPattern = (s) => /^%|%$|^!/.test(s);

        try {
            const selectorMap = normalizeRelationSelectorArg(selector);
            for (const [nsNameMaybeWildcard, tblNames] of Object.entries(selectorMap)) {

                const _nsNames = [nsNameMaybeWildcard];
                const nsNames = nsNameMaybeWildcard === '*'
                    ? tx.listNamespaces()
                    : (isPattern(nsNameMaybeWildcard)
                        ? tx.listNamespaces().sort().filter((s) => matchRelationSelector(s, _nsNames))
                        : _nsNames);

                for (const nsName of nsNames) {
                    for (const tblNameMaybeWildcard of tblNames) {

                        const _tblNames = [tblNameMaybeWildcard];
                        const tblNames = tblNameMaybeWildcard === '*'
                            ? tx.listTables({ namespace: nsName }).sort()
                            : (isPattern(tblNameMaybeWildcard)
                                ? tx.listTables({ namespace: nsName }).sort().filter((s) => matchRelationSelector(s, _tblNames))
                                : _tblNames);

                        for (const tblName of tblNames) {
                            const handlerReturn = handle(tx, nsName, tblName);
                            if (handlerMode === 'async') await handlerReturn;
                        }
                    }
                }
            }

            await tx.commit();
        } catch (e) {
            await tx.rollback();
            throw e;
        }
    }

    // ----- transactions

    async transaction(cb, options = {}) {
        if (typeof cb !== 'function') {
            throw new TypeError('transaction(cb): cb must be a function');
        }

        const tx = this.begin(options);
        let returnValue;

        try {
            returnValue = await cb(tx);
            await tx.commit();
        } catch (e) {
            await tx.rollback();
            throw e;
        }

        return returnValue;
    }

    async commit(tx) {
        let timestamp;
        let queuedSyncSelector = null;
        if (!this.#isHydrating) {
            if (this.#overwriteForward && !this.#forwardHistoryTruncated && tx._changeLog.length) {
                await this.#wal.truncateForward(this.#openedCommitTime);
                this.#forwardHistoryTruncated = true;
            }
            timestamp = Date.now();
            await this.#federateCommit(tx, timestamp);
            if (this.#autoSync) {
                queuedSyncSelector = this.#queuedSyncSelectorFor(tx);
            }
        }

        const returnValue = await super.commit(tx);

        if (!this.#isHydrating) {
            await this.#persistCommit(tx, timestamp);
            // Keep the original versionStop anchor intact until first mutating commit
            // performs forward-history truncation.
            if (!(this.#overwriteForward && !this.#forwardHistoryTruncated && !tx._changeLog.length)) {
                this.#openedCommitTime = this.txMeta(tx.id)?.commitTime;
            }
            this.#scheduleAutoSync(queuedSyncSelector);
        }

        return returnValue;
    }
}
